import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/shared/auth/useAuth';

import { registrationErrorMessage, useRegisterCompany } from '@/features/registration';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Public company sign-up wizard → provisions a tenant + owner via the BFF. */
export function SignUpPage() {
  const { t } = useTranslation('app');
  const { login } = useAuth();
  const navigate = useNavigate();
  const STEPS = useMemo(() => [t('signUp.steps.company'), t('signUp.steps.owner'), t('signUp.steps.review')], [t]);
  const [step, setStep] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');

  const mutation = useRegisterCompany();

  const companyValid = companyName.trim().length >= 2;
  const ownerValid = ownerName.trim().length >= 2 && EMAIL_RE.test(ownerEmail.trim());

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = () => {
    mutation.mutate({
      companyName: companyName.trim(),
      ownerName: ownerName.trim(),
      ownerEmail: ownerEmail.trim(),
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 520 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          {mutation.isSuccess ? (
            <Stack spacing={2} alignItems="center" textAlign="center">
              <MarkEmailReadRoundedIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="h5">{t('signUp.success.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('signUp.success.message', { email: ownerEmail, company: companyName })}
              </Typography>
              <Button variant="contained" onClick={login}>
                {t('signUp.success.goToSignIn')}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" component="h1">
                  {t('signUp.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('signUp.subtitle')}
                </Typography>
              </Box>

              <Stepper activeStep={step} alternativeLabel>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {mutation.isError ? (
                <Alert severity="error">{registrationErrorMessage(mutation.error)}</Alert>
              ) : null}

              {step === 0 ? (
                <TextField
                  label={t('signUp.companyNameLabel')}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  fullWidth
                  required
                  autoFocus
                  helperText={t('signUp.companyNameHelper')}
                />
              ) : null}

              {step === 1 ? (
                <Stack spacing={2}>
                  <TextField
                    label={t('signUp.yourNameLabel')}
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label={t('signUp.workEmailLabel')}
                    type="email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    fullWidth
                    required
                    error={ownerEmail.length > 0 && !EMAIL_RE.test(ownerEmail.trim())}
                    helperText={t('signUp.workEmailHelper')}
                  />
                </Stack>
              ) : null}

              {step === 2 ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">{t('signUp.steps.review')}</Typography>
                  <ReviewRow label={t('signUp.steps.company')} value={companyName} />
                  <ReviewRow label={t('signUp.steps.owner')} value={ownerName} />
                  <ReviewRow label={t('signUp.review.email')} value={ownerEmail} />
                </Stack>
              ) : null}

              <Stack direction="row" justifyContent="space-between">
                <Button
                  startIcon={<ArrowBackRoundedIcon />}
                  color="inherit"
                  onClick={step === 0 ? () => navigate('/welcome') : back}
                  disabled={mutation.isPending}
                >
                  {step === 0 ? t('signUp.signIn') : t('signUp.back')}
                </Button>

                {step < STEPS.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={next}
                    disabled={step === 0 ? !companyValid : !ownerValid}
                  >
                    {t('signUp.continueButton')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={submit}
                    disabled={mutation.isPending || !companyValid || !ownerValid}
                    startIcon={
                      mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
                    }
                  >
                    {t('signUp.createCompany')}
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value || '—'}
      </Typography>
    </Stack>
  );
}
